import { useCallback, useEffect, useRef, useState } from 'react'
import { useFleet } from '../state/store'
import { pollWhileVisible } from '../lib/pollWhileVisible'
import { vehicleState } from '../lib/format'
import type { Breakdown, Vehicle } from '../lib/types'
import {
  fetchMbmtBreakdowns, fetchMbmtLive, type MbmtVehicle,
} from '../busOps/useMbmtApi'
import {
  fetchTuplBreakdowns, fetchTuplLive, type TuplVehicle,
} from '../busOps/tupl/useTuplApi'
import { fetchEimLive, type EimVehicle } from '../eim/useEimApi'
import { fetchStarBreakdowns, fetchStarLive, type StarVehicle } from '../star/useStarApi'
import { fetchBusBoard, fetchBusBreakdowns } from '../busOps/useBusApi'
import type { BusBoardRow } from '../busOps/types'
import { busModel, type BusModel } from '../busOps/busIcons'

export type GlobalFleet = 'ultratech' | 'mbmt' | 'eim' | 'star' | 'nagpur' | 'wondercement' | 'tupl'
/** The one status vocabulary every fleet's raw signal gets mapped onto. Matches
 *  console/src/map/layers/markers.ts's MarkerState exactly (the Ultratech corridor map's
 *  own canonical five states) rather than inventing a sixth vocabulary for this map --
 *  MBMT and EIM simply don't populate every state (neither tracks 'charging' the same
 *  way, MBMT alone tracks 'down'), which is a real difference in what's measured, not a
 *  reason to rename the states that DO apply. */
export type GlobalState = 'moving' | 'idle' | 'charging' | 'stale' | 'down'

export interface GlobalVehicle {
  fleet: GlobalFleet
  id: string
  lat: number
  lon: number
  heading: number
  speedKph: number | null
  socPct: number | null
  state: GlobalState
  /** One line of fleet-specific context for the popup -- a route code, a port site, a
   *  driver name. Optional because Ultratech has nothing more specific to add than what
   *  the generic fields already show. */
  sub: string | null
  /** MBMT only -- which bus glyph (busIcons.ts) to draw. Null for the other two fleets,
   *  which always draw the truck glyph regardless of this field. */
  busModel: BusModel | null
}

export interface GlobalCounts {
  total: number
  ultratech: number
  mbmt: number
  eim: number
  star: number
  nagpur: number
  wondercement: number
  tupl: number
  moving: number
  idle: number
  charging: number
  /** down + stale, combined -- a supervisory glance needs "how many need a look", not two
   *  separate small numbers for two different reasons a vehicle might need one. */
  attention: number
}

function ultratechToGlobal(vehicles: Map<string, Vehicle>, breakdowns: Map<string, Breakdown>, now: number): GlobalVehicle[] {
  const out: GlobalVehicle[] = []
  for (const v of vehicles.values()) {
    // vehicles is shared across every non-diverted fleet (Ultratech + Wonder Cement, as
    // of 2026-10) -- Wonder Cement carries its own 'transvolt-wondercement' tag and is
    // split out by wondercementToGlobal() below. An untagged vehicle (no v.fleet at all)
    // defaults to Ultratech, same as it always has.
    if (v.fleet && v.fleet !== 'transvolt-ultratech') continue
    if (v.lat == null || v.lon == null) continue
    const state: GlobalState = breakdowns.has(v.vehicle_id) ? 'down' : vehicleState(v, now)
    out.push({
      fleet: 'ultratech',
      id: v.vehicle_id,
      lat: v.lat,
      lon: v.lon,
      heading: v.heading_deg ?? 0,
      speedKph: v.speed_kph,
      socPct: v.soc_pct,
      state,
      sub: null,
      busModel: null,
    })
  }
  return out
}

function wondercementToGlobal(vehicles: Map<string, Vehicle>, breakdowns: Map<string, Breakdown>, now: number): GlobalVehicle[] {
  const out: GlobalVehicle[] = []
  for (const v of vehicles.values()) {
    if (v.fleet !== 'transvolt-wondercement') continue
    if (v.lat == null || v.lon == null) continue
    const state: GlobalState = breakdowns.has(v.vehicle_id) ? 'down' : vehicleState(v, now)
    out.push({
      fleet: 'wondercement',
      id: v.vehicle_id,
      lat: v.lat,
      lon: v.lon,
      heading: v.heading_deg ?? 0,
      speedKph: v.speed_kph,
      socPct: v.soc_pct,
      state,
      sub: null,
      busModel: null,
    })
  }
  return out
}

/** MBMT's own stale bar (MbmtLiveMap.tsx's busStatus/BUS_STATUS_STYLE): 90s, not the
 *  45-minute STALE_AFTER_MS Ultratech uses -- MBMT buses report roughly every 60s, so 90s
 *  is one missed poll's worth of grace, the same margin its own live map already applies. */
const MBMT_STALE_S = 90

function mbmtToGlobal(vehicles: MbmtVehicle[], down: Set<string>): GlobalVehicle[] {
  const out: GlobalVehicle[] = []
  for (const v of vehicles) {
    if (v.lat == null || v.lon == null) continue
    const state: GlobalState = down.has(v.vehicle_id)
      ? 'down'
      : (!v.on_air || v.seen_s >= MBMT_STALE_S)
        ? 'stale'
        : v.moving ? 'moving' : 'idle'
    out.push({
      fleet: 'mbmt',
      id: v.vehicle_id,
      lat: v.lat,
      lon: v.lon,
      heading: v.heading_deg ?? 0,
      speedKph: v.speed_kph,
      socPct: v.soc_pct,
      state,
      sub: v.route_code ? `route ${v.route_code}` : null,
      busModel: busModel(v.vehicletype),
    })
  }
  return out
}

// Mirrors mbmtToGlobal exactly (same MBMT_STALE_S bar, same state derivation) --
// TUPL's own site, independent `down` set, own 'tupl' fleet tag. Exported (unlike
// mbmtToGlobal) so useGlobalApiTupl.test.ts can test the pure mapper directly.
export function tuplToGlobal(vehicles: TuplVehicle[], down: Set<string>): GlobalVehicle[] {
  const out: GlobalVehicle[] = []
  for (const v of vehicles) {
    if (v.lat == null || v.lon == null) continue
    const state: GlobalState = down.has(v.vehicle_id)
      ? 'down'
      : (!v.on_air || v.seen_s >= MBMT_STALE_S)
        ? 'stale'
        : v.moving ? 'moving' : 'idle'
    out.push({
      fleet: 'tupl',
      id: v.vehicle_id,
      lat: v.lat,
      lon: v.lon,
      heading: v.heading_deg ?? 0,
      speedKph: v.speed_kph,
      socPct: v.soc_pct,
      state,
      sub: v.route_code ? `route ${v.route_code}` : null,
      busModel: busModel(v.vehicletype),
    })
  }
  return out
}

/** Matches EimShell/EimPortsMap's own 5-minute offline bar exactly (EIM_POLL_S is 60s
 *  server-side, so 5min tolerates several missed polls before calling it stale). */
const EIM_STALE_MS = 5 * 60_000

function eimToGlobal(vehicles: EimVehicle[], now: number): GlobalVehicle[] {
  const out: GlobalVehicle[] = []
  for (const v of vehicles) {
    if (v.lat == null || v.lng == null) continue
    const staleMs = now - new Date(v.polled_ts).getTime()
    // 'parked' (see useEimApi.ts) has no equivalent bucket in this map's simpler five-state
    // model -- it folds into 'idle' rather than the moving/charging fallthrough this used
    // to have, which would have shown a switched-off, zero-draw truck as green "moving".
    const state: GlobalState = staleMs > EIM_STALE_MS
      ? 'stale'
      : v.status === 'charging' ? 'charging'
      : v.status === 'moving' ? 'moving'
      : 'idle'
    out.push({
      fleet: 'eim',
      id: v.vehicle_id,
      lat: v.lat,
      lon: v.lng,
      heading: v.heading ?? 0,
      speedKph: v.gps_speed,
      socPct: v.soc,
      state,
      sub: v.site === 'gti' ? 'GTI' : 'JM Baxi',
      busModel: null,
    })
  }
  return out
}

// STAR's own live view (console/src/star/StarMap.tsx) computes a real heading from
// consecutive fixes for its truck-rotation glyph -- this supervisory overview doesn't
// carry that per-vehicle state, so heading falls back to 0 (north) here, same fallback
// every other fleet already uses when its source has no course/heading field.
const STAR_STALE_MS = 60_000 // PROPEL reports every ~5s; a full minute of silence is real
const STAR_MOVING_KPH = 2 // clears GPS-jitter-only "speed" reads on a genuinely parked truck

function starToGlobal(vehicles: StarVehicle[], down: Set<string>, now: number): GlobalVehicle[] {
  const out: GlobalVehicle[] = []
  for (const v of vehicles) {
    if (v.latitude == null || v.longitude == null) continue
    const staleMs = now - new Date(v.polled_ts).getTime()
    const state: GlobalState = down.has(v.vehicle_id)
      ? 'down'
      : staleMs > STAR_STALE_MS
        ? 'stale'
        : v.charging_status === 1
          ? 'charging'
          : (v.speed ?? 0) > STAR_MOVING_KPH ? 'moving' : 'idle'
    out.push({
      fleet: 'star',
      id: v.vehicle_id,
      lat: v.latitude,
      lon: v.longitude,
      heading: 0,
      speedKph: v.speed,
      socPct: v.soc,
      state,
      sub: 'STAR Cement',
      busModel: null,
    })
  }
  return out
}

// Same 5-minute stale bar and >3 kph moving threshold NagpurBusOpsShell.tsx's own
// boardVehicleStatus() uses for the Fleet drawer -- duplicated rather than imported (that
// function is local/unexported, and every other fleet in this file already re-derives its
// own status locally rather than sharing one across files).
const NAGPUR_STALE_MS = 5 * 60_000
const NAGPUR_MOVING_KPH = 3

function nagpurToGlobal(vehicles: BusBoardRow[], down: Set<string>, now: number): GlobalVehicle[] {
  const out: GlobalVehicle[] = []
  for (const v of vehicles) {
    if (v.lat == null || v.lon == null) continue
    const state: GlobalState = down.has(v.vehicle_id)
      ? 'down'
      : (v.updated_ts == null || now - v.updated_ts > NAGPUR_STALE_MS)
        ? 'stale'
        : (v.speed_kph ?? 0) > NAGPUR_MOVING_KPH ? 'moving' : 'idle'
    out.push({
      fleet: 'nagpur',
      id: v.vehicle_id,
      lat: v.lat,
      lon: v.lon,
      heading: v.heading_deg ?? 0,
      speedKph: v.speed_kph,
      socPct: v.soc_pct,
      state,
      sub: 'Nagpur (NTSPL)',
      busModel: null,
    })
  }
  return out
}

function emptyCounts(): GlobalCounts {
  return {
    total: 0, ultratech: 0, mbmt: 0, eim: 0, star: 0, nagpur: 0, wondercement: 0, tupl: 0,
    moving: 0, idle: 0, charging: 0, attention: 0,
  }
}

export function tally(vehicles: GlobalVehicle[]): GlobalCounts {
  const c = emptyCounts()
  for (const v of vehicles) {
    c.total += 1
    c[v.fleet] += 1
    if (v.state === 'moving') c.moving += 1
    else if (v.state === 'idle') c.idle += 1
    else if (v.state === 'charging') c.charging += 1
    else c.attention += 1
  }
  return c
}

/**
 * Ultratech comes off the store that useFleetData() already keeps hydrated app-wide
 * (console/src/app/AppShell.tsx calls it once, unconditionally, at the Console level) --
 * fetching /api/state again here would be a second poller for data this page already has
 * for free. MBMT and EIM have no such standing store, so they're polled directly here,
 * same 10s cadence EimShell's own LiveView already uses (their pollers refresh server-side
 * on a slower cycle than that, so polling faster just re-fetches the same snapshot).
 */
export function useGlobalFleet(): { vehicles: GlobalVehicle[]; counts: GlobalCounts; loaded: boolean; err: string | null } {
  const ultraVehicles = useFleet((s) => s.vehicles)
  const ultraBreakdowns = useFleet((s) => s.breakdowns)
  const [mbmt, setMbmt] = useState<MbmtVehicle[]>([])
  const [mbmtDown, setMbmtDown] = useState<Set<string>>(new Set())
  const [tupl, setTupl] = useState<TuplVehicle[]>([])
  const [tuplDown, setTuplDown] = useState<Set<string>>(new Set())
  const [eim, setEim] = useState<EimVehicle[]>([])
  const [star, setStar] = useState<StarVehicle[]>([])
  const [starDown, setStarDown] = useState<Set<string>>(new Set())
  const [nagpur, setNagpur] = useState<BusBoardRow[]>([])
  const [nagpurDown, setNagpurDown] = useState<Set<string>>(new Set())
  const [err, setErr] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const loadedOnce = useRef(false)
  // Guards against the 10s interval below starting a fresh round of independent fetch
  // groups on top of a round still in flight -- see BusOpsShell.tsx's own
  // refreshInFlightRef for the incident this class of bug caused there. The
  // Promise.allSettled split above already stops one slow group from blocking the
  // OTHERS within a round; this stops one slow round from piling onto the NEXT one.
  const refreshInFlightRef = useRef(false)

  // Five INDEPENDENT fleet-group fetches (mbmt, tupl, eim, star, nagpur -- ultratech and
  // wondercement both read from the always-on store, no fetch group of their own), not
  // one Promise.all across all nine requests. A single Promise.all used to gate every
  // non-Ultratech fleet's setState behind whichever one was slowest -- confirmed live
  // (2026-10-07) as the reason
  // this tab "loads ultratech instantly" (ultraVehicles comes from the always-on store,
  // untouched by this fetch) while MBMT/EIM/STAR/Nagpur all sat blank together even once
  // most of their own requests had already come back, because one slow or Postgres-
  // contention-stalled breakdowns query held up the Promise.all as a whole. Each group now
  // sets its own state the moment IT resolves; a slow Nagpur board no longer blocks MBMT,
  // EIM, or STAR from appearing.
  const refresh = useCallback(async () => {
    if (refreshInFlightRef.current) return
    refreshInFlightRef.current = true
    const tasks = [
      (async () => {
        const [live, breakdowns] = await Promise.all([fetchMbmtLive(), fetchMbmtBreakdowns(true)])
        setMbmt(live.vehicles)
        setMbmtDown(new Set(breakdowns.map((b) => b.vehicle_id)))
      })(),
      (async () => {
        const [live, breakdowns] = await Promise.all([fetchTuplLive(), fetchTuplBreakdowns(true)])
        setTupl(live.vehicles)
        setTuplDown(new Set(breakdowns.map((b) => b.vehicle_id)))
      })(),
      (async () => {
        setEim(await fetchEimLive('all'))
      })(),
      (async () => {
        const [starLive, starBreakdowns] = await Promise.all([fetchStarLive(), fetchStarBreakdowns(true)])
        setStar(starLive)
        setStarDown(new Set(starBreakdowns.map((b) => b.vehicle_id)))
      })(),
      (async () => {
        const [nagpurLive, nagpurBreakdowns] = await Promise.all([fetchBusBoard('NAGPUR'), fetchBusBreakdowns(true)])
        setNagpur(nagpurLive)
        setNagpurDown(new Set(nagpurBreakdowns.map((b) => b.vehicle_id)))
      })(),
    ]
    const results = await Promise.allSettled(tasks)
    const failed = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected')
    const firstReason = failed[0]?.reason
    setErr(failed.length ? (firstReason instanceof Error ? firstReason.message : 'Failed to load the global fleet map') : null)
    loadedOnce.current = true
    setLoaded(true)
    refreshInFlightRef.current = false
  }, [])

  useEffect(() => pollWhileVisible(() => void refresh(), 10_000), [refresh])

  const now = Date.now()
  const vehicles = [
    ...ultratechToGlobal(ultraVehicles, ultraBreakdowns, now),
    ...mbmtToGlobal(mbmt, mbmtDown),
    ...tuplToGlobal(tupl, tuplDown),
    ...eimToGlobal(eim, now),
    ...starToGlobal(star, starDown, now),
    ...nagpurToGlobal(nagpur, nagpurDown, now),
    ...wondercementToGlobal(ultraVehicles, ultraBreakdowns, now),
  ]
  return { vehicles, counts: tally(vehicles), loaded, err }
}
